import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';

const Hero = () => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const [animationData, setAnimationData] = useState(null);
  const lottieRef = useRef(null);
  const [typedText, setTypedText] = useState('');
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const typingSentences = t('hero.typingSentences', { returnObjects: true });
  const features = [
    t('hero.features.ai'),
    t('hero.features.autonomous'),
    t('hero.features.future'),
  ];

  useEffect(() => {
    fetch('/videos/data.json')
      .then(response => response.json())
      .then(data => setAnimationData(data))
      .catch(error => console.error('Error loading Lottie animation:', error));
  }, []);

  // Reset typing animation when language changes
  useEffect(() => {
    setTypedText('');
    setCurrentSentenceIndex(0);
    setIsDeleting(false);
  }, [i18n.language]);

  // Continuous typing animation effect
  useEffect(() => {
    const currentSentence = typingSentences[currentSentenceIndex];
    let timeout;

    if (!isDeleting && typedText === currentSentence) {
      // Pause at the end of typing
      timeout = setTimeout(() => {
        setIsDeleting(true);
      }, 2500); // Wait 2.5 seconds before deleting
    } else if (isDeleting && typedText === '') {
      // Move to next sentence after deleting
      setIsDeleting(false);
      setCurrentSentenceIndex((prev) => (prev + 1) % typingSentences.length);
    } else {
      // Type or delete characters
      timeout = setTimeout(() => {
        if (isDeleting) {
          setTypedText(currentSentence.slice(0, typedText.length - 1));
        } else {
          setTypedText(currentSentence.slice(0, typedText.length + 1));
        }
      }, isDeleting ? 15 : 120); // Fast deleting, slow typing
    }

    return () => clearTimeout(timeout);
  }, [typedText, isDeleting, currentSentenceIndex, typingSentences]);

  return (
    <section
      id="hero"
      className={`min-h-[100vh] flex items-center justify-center relative overflow-hidden pt-16 pb-8 ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-afsc-slate via-afsc-slate to-afsc-gold-deep/20' 
          : 'bg-gradient-to-br from-afsc-offwhite via-afsc-offwhite to-afsc-gold/10'
      }`}
      style={{
        background: theme === 'dark'
          ? 'radial-gradient(circle at center, rgba(203, 163, 90, 0.35) 0%, rgba(17, 20, 28, 1) 50%, rgba(17, 20, 28, 1) 100%)'
          : 'radial-gradient(circle at center, rgba(203, 163, 90, 0.2) 0%, rgba(248, 245, 240, 1) 50%, rgba(248, 245, 240, 1) 100%)',
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-afsc-gold/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-afsc-gold/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
      </div>

      {/* Flying animated elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating circles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={`circle-${i}`}
            className={`absolute rounded-full border ${theme === 'dark' ? 'border-afsc-gold/20' : 'border-afsc-gold/40'}`}
            style={{
              width: `${20 + i * 15}px`,
              height: `${20 + i * 15}px`,
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              opacity: theme === 'dark' ? [0.2, 0.5, 0.2] : [0.3, 0.6, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.3,
            }}
          />
        ))}

        {/* Floating geometric shapes */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`shape-${i}`}
            className="absolute"
            style={{
              width: `${30 + i * 10}px`,
              height: `${30 + i * 10}px`,
              right: `${15 + i * 20}%`,
              top: `${30 + (i % 2) * 40}%`,
            }}
            animate={{
              y: [0, -40, 0],
              x: [0, -25, 0],
              rotate: [0, 180, 360],
              opacity: theme === 'dark' ? [0.15, 0.4, 0.15] : [0.25, 0.6, 0.25],
            }}
            transition={{
              duration: 4 + i * 0.7,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.5,
            }}
          >
            <div className={`w-full h-full border-2 ${theme === 'dark' ? 'border-afsc-gold/30' : 'border-afsc-gold/50'} rotate-45`} />
          </motion.div>
        ))}

        {/* Floating particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className={`absolute w-2 h-2 ${theme === 'dark' ? 'bg-afsc-gold/40' : 'bg-afsc-gold/60'} rounded-full`}
            style={{
              left: `${5 + (i % 4) * 25}%`,
              top: `${10 + Math.floor(i / 4) * 50}%`,
            }}
            animate={{
              y: [0, -50, 0],
              x: [0, Math.sin(i) * 30, 0],
              opacity: theme === 'dark' ? [0, 0.6, 0] : [0, 0.8, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2.5 + i * 0.3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.2,
            }}
          />
        ))}

        {/* Floating lines */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`line-${i}`}
            className={`absolute h-px bg-gradient-to-r from-transparent ${theme === 'dark' ? 'via-afsc-gold/30' : 'via-afsc-gold/50'} to-transparent`}
            style={{
              width: `${100 + i * 50}px`,
              left: `${20 + i * 30}%`,
              top: `${25 + i * 30}%`,
            }}
            animate={{
              y: [0, -60, 0],
              x: [0, 40, 0],
              opacity: theme === 'dark' ? [0, 0.5, 0] : [0, 0.7, 0],
              rotate: [0, 45, 0],
            }}
            transition={{
              duration: 5 + i,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.8,
            }}
          />
        ))}
      </div>

      {/* Bottom fade gradient to blend with About section */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        aria-hidden="true"
        style={{
          background: theme === 'dark'
            ? 'linear-gradient(to bottom, transparent 0%, rgba(17, 20, 28, 1) 100%)'
            : 'linear-gradient(to bottom, transparent 0%, rgba(248, 245, 240, 1) 100%)',
          zIndex: 2,
        }}
      />

      {/* Lottie Animation - Absolutely positioned at bottom, 30% cut off */}
      {animationData && (
        <motion.div
          className="absolute left-0 right-0 bottom-0 flex items-end justify-center pointer-events-none overflow-hidden"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          style={{ zIndex: 1, height: '100%', paddingBottom: '-30%' }}
        >
          <motion.div 
            className="relative w-full max-w-[800px]"
            style={{ 
              background: 'transparent',
              transform: 'translateY(30%)',
            }}
            animate={{
              filter: [
                'drop-shadow(0 0 20px rgba(203, 163, 90, 0.4))',
                'drop-shadow(0 0 40px rgba(203, 163, 90, 0.8))',
                'drop-shadow(0 0 20px rgba(203, 163, 90, 0.4))',
              ],
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <Lottie
              lottieRef={lottieRef}
              animationData={animationData}
              loop={true}
              autoplay={true}
              speed={1}
              renderer="svg"
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid meet',
                clearCanvas: false,
                className: 'lottie-animation',
              }}
              onComplete={() => {
                if (lottieRef.current) {
                  lottieRef.current.goToAndPlay(0, true);
                }
              }}
              style={{
                maxHeight: '600px',
                maxWidth: '100%',
                width: '100%',
                height: 'auto',
                filter: 'blur(3px) drop-shadow(0 0 30px rgba(203, 163, 90, 0.6))',
                backgroundColor: 'transparent',
              }}
            />
          </motion.div>
        </motion.div>
      )}

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10 -mt-16">
        <div className="flex flex-col items-center justify-center text-center">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 2.0 }}
            className="mb-3"
          >
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-3 leading-tight  `} >
              <span 
                className="text-afsc-gold inline-block min-h-[1.2em]" 
                style={{ 
                  display: 'block',
                  width: '100%'
                }}
              >
                {typedText}
                <motion.span
                  className={`inline-block ${i18n.language === 'ar' ? 'mr-0.5' : 'ml-0.5'}`}
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                >
                  |
                </motion.span>
              </span>
              {/* <motion.span 
                className={theme === 'dark' ? 'text-white' : 'text-afsc-charcoal'}
                initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 2.0 }}
              >
                Digital solutions
              </motion.span> */}
              {/* <motion.span 
                className={theme === 'dark' ? 'text-white' : 'text-afsc-charcoal'}
                initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                viewport={{ once: true }}
                transition={{ delay: 0.9, duration: 2.0 }}
              >
                Built for growth
              </motion.span> */}
            </h1>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ delay: 1.2, duration: 2.0 }}
            className="mb-6 max-w-3xl"
          >
            <p className={`text-lg md:text-xl mb-6 leading-relaxed ${
              theme === 'dark' ? 'text-white/80' : 'text-afsc-charcoal/80'
            }`}>
              {t('hero.description')}
            </p>
          </motion.div>

          {/* Feature badges */}
          <motion.div
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ delay: 1.5, duration: 2.0 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {features.map((feature, index) => (
              <motion.span
                key={feature}
                initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                viewport={{ once: true }}
                transition={{ delay: 1.8 + index * 0.2, duration: 2.0 }}
                className={`px-4 py-2 backdrop-blur-sm rounded-full text-sm border ${
                  theme === 'dark'
                    ? 'bg-white/10 text-white/90 border-white/20'
                    : 'bg-afsc-charcoal/10 text-afsc-charcoal/90 border-afsc-charcoal/20'
                }`}
              >
                {feature}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

